import React from 'react';
import { motion } from 'framer-motion';
import { FaServer, FaChartLine, FaCheckCircle, FaClock } from 'react-icons/fa';
import './ApiPerformance.css';

const ApiPerformance = () => {
  const apiFeatures = [
    {
      icon: '🔌',
      title: 'REST API Testing',
      description: 'Complete validation of RESTful endpoints with all HTTP methods'
    },
    {
      icon: '🔐',
      title: 'Authentication Testing',
      description: 'JWT, Bearer Token, OAuth, and API Key authentication validation'
    },
    {
      icon: '📋',
      title: 'Schema Validation',
      description: 'JSON schema validation and response structure verification'
    },
    {
      icon: '⚡',
      title: 'Status Code Verification',
      description: 'Comprehensive HTTP status code validation (200, 201, 400, 401, 404, 500)'
    },
    {
      icon: '🔄',
      title: 'Integration Testing',
      description: 'End-to-end API workflow and integration testing'
    },
    {
      icon: '📊',
      title: 'Response Time Analysis',
      description: 'Performance metrics and response time validation'
    }
  ];

  const postmanFeatures = [
    'Collection organization with folders',
    'Environment variables for different environments',
    'Pre-request scripts for dynamic data',
    'Test scripts with assertions',
    'Data-driven testing with CSV/JSON',
    'Newman for CLI execution',
    'CI/CD integration'
  ];

  const performanceTests = [
    {
      type: 'Load Testing',
      icon: '📈',
      color: '#3b82f6',
      description: 'Validate system behavior under expected load',
      metrics: ['Concurrent Users: 100-1000', 'Duration: 30-60 min', 'Ramp-up: Gradual']
    },
    {
      type: 'Stress Testing',
      icon: '💪',
      color: '#ef4444',
      description: 'Test system limits and breaking points',
      metrics: ['Concurrent Users: 1000+', 'Duration: Until failure', 'Ramp-up: Aggressive']
    },
    {
      type: 'Spike Testing',
      icon: '⚡',
      color: '#f59e0b',
      description: 'Sudden traffic surge handling',
      metrics: ['Sudden spike: 0 to 1000', 'Duration: 5-10 min', 'Pattern: Immediate']
    },
    {
      type: 'Endurance Testing',
      icon: '⏱️',
      color: '#10b981',
      description: 'Long-term stability and memory leaks',
      metrics: ['Concurrent Users: 500', 'Duration: 8-24 hours', 'Pattern: Sustained']
    }
  ];

  const performanceMetrics = [
    { label: 'Response Time', value: '< 2 seconds', icon: <FaClock /> },
    { label: 'Throughput', value: '1000 req/sec', icon: <FaChartLine /> },
    { label: 'Error Rate', value: '< 1%', icon: <FaCheckCircle /> },
    { label: 'Concurrent Users', value: '10,000+', icon: <FaServer /> }
  ];

  return (
    <section id="api-performance" className="api-performance">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">API & Performance Testing</h2>
          <p className="section-subtitle">
            Comprehensive API validation and performance testing expertise
          </p>
        </motion.div>

        <div className="api-section">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaServer className="section-icon" />
            <h3>API Testing</h3>
          </motion.div>

          <div className="api-content">
            <div className="api-features-grid">
              {apiFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  className="api-feature-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="api-feature-icon">{feature.icon}</div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="postman-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <div className="postman-header">
                <h4>Postman Expertise</h4>
                <span className="postman-badge">Advanced</span>
              </div>
              <div className="postman-features">
                {postmanFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="postman-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7 + index * 0.05 }}
                  >
                    <FaCheckCircle className="check-icon" />
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        <div className="performance-section">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaChartLine className="section-icon" />
            <h3>Performance Testing</h3>
          </motion.div>

          <div className="performance-types-grid">
            {performanceTests.map((test, index) => (
              <motion.div
                key={index}
                className="performance-type-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="perf-header" style={{ backgroundColor: `${test.color}15` }}>
                  <div className="perf-icon" style={{ color: test.color }}>
                    {test.icon}
                  </div>
                  <h4 style={{ color: test.color }}>{test.type}</h4>
                </div>
                <p className="perf-description">{test.description}</p>
                <div className="perf-metrics">
                  {test.metrics.map((metric, idx) => (
                    <div key={idx} className="metric-item">
                      <span className="metric-bullet" style={{ backgroundColor: test.color }}></span>
                      {metric}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="performance-metrics"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
          >
            <h4>Performance Benchmarks</h4>
            <div className="metrics-grid">
              {performanceMetrics.map((metric, index) => (
                <motion.div
                  key={index}
                  className="metric-card"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <div className="metric-icon">{metric.icon}</div>
                  <div className="metric-value">{metric.value}</div>
                  <div className="metric-label">{metric.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="jmeter-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.9 }}
          >
            <h4>JMeter Test Scenarios</h4>
            <div className="jmeter-scenarios">
              <div className="scenario-card">
                <h5>Virtual Users Configuration</h5>
                <p>Thread groups with ramp-up period and loop count for realistic user simulation</p>
              </div>
              <div className="scenario-card">
                <h5>Result Analysis</h5>
                <p>Aggregate reports, graphs, and response time analysis for performance insights</p>
              </div>
              <div className="scenario-card">
                <h5>Bottleneck Identification</h5>
                <p>Database queries, API endpoints, and server resource utilization analysis</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ApiPerformance;
